import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  IconButton,
} from '@mui/material';
import {
  Event as EventIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Class as ClassIcon,
  CheckCircle as CheckCircleIcon,
  Stars as StarsIcon,
  Assignment as AssignmentIcon,
  HowToReg as HowToRegIcon,
  Info as InfoIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ScholarshipDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { scholarship } = state || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!scholarship) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 3 }}>
          No scholarship data available
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/scholarships')}
          sx={{ bgcolor: '#6200ee', '&:hover': { bgcolor: '#7f39fb' } }}
        >
          Back to Scholarships
        </Button>
      </Box>
    );
  }

  const renderDetailRow = (icon, label, value) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box
        sx={{
          bgcolor: '#6200ee',
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon sx={{ color: '#6200ee' }} />
          </IconButton>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
              {scholarship.title}
            </Typography>
            <Typography variant="h6" sx={{ color: '#6200ee', fontWeight: 600 }}>
              {scholarship.hostOrganization}
            </Typography>
          </Box>
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6200ee', mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
            Key Information
          </Typography>
          {renderDetailRow(
            <EventIcon sx={{ color: '#fff' }} />,
            'Deadline',
            new Date(scholarship.deadline).toLocaleDateString()
          )}
          {renderDetailRow(<SchoolIcon sx={{ color: '#fff' }} />, 'Type', scholarship.type)}
          {renderDetailRow(<PeopleIcon sx={{ color: '#fff' }} />, 'Level', scholarship.level)}
          {renderDetailRow(<PublicIcon sx={{ color: '#fff' }} />, 'Countries', scholarship.eligibleCountries)}
          {renderDetailRow(<ClassIcon sx={{ color: '#fff' }} />, 'Fields', scholarship.fieldsOfStudy)}
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#6200ee', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Eligibility Criteria
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6 }}>
            {scholarship.eligibilityCriteria}
          </Typography>
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StarsIcon sx={{ color: '#6200ee', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Benefits
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6, mb: scholarship.duration ? 2 : 0 }}>
            {scholarship.benefits}
          </Typography>
          {scholarship.duration && (
            <Box sx={{ bgcolor: '#f0e8ff', p: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#6200ee' }}>
                <strong>Duration: </strong>{scholarship.duration}
              </Typography>
            </Box>
          )}
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssignmentIcon sx={{ color: '#6200ee', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Application Process
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6, mb: scholarship.selectionProcess ? 2 : 0 }}>
            {scholarship.applicationProcess}
          </Typography>
          {scholarship.selectionProcess && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 2 }}>
                <HowToRegIcon sx={{ color: '#6200ee', mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  Selection Process
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6 }}>
                {scholarship.selectionProcess}
              </Typography>
            </>
          )}
        </Paper>
      </motion.div>

      {scholarship.additionalNotes && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Paper
            sx={{
              p: 3,
              mb: 3,
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #6200ee',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ color: '#6200ee', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                Additional Notes
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.6 }}>
              {scholarship.additionalNotes}
            </Typography>
          </Paper>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        whileHover={{ scale: 1.05 }}
      >
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            href={scholarship.officialLink}
            target="_blank"
            sx={{
              bgcolor: '#6200ee',
              '&:hover': { bgcolor: '#7f39fb' },
              borderRadius: 30,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
            }}
          >
            Apply Now
          </Button>
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
            Need help with your application?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin-support-dashboard')}
            sx={{
              borderColor: '#6200ee',
              color: '#6200ee',
              borderRadius: 20,
              px: 3,
              py: 1,
            }}
          >
            Contact Support
          </Button>
        </Box>
      </motion.div>
    </Container>
  );
};

export default ScholarshipDetails;